// src/components/admin/CandidateFormModal.js
import React, { useState, useRef, useEffect } from 'react';
import ReactCrop, { makeAspectCrop, centerCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { electionApi } from '../../api/election';
import Select from 'react-select';

const CandidateFormModal = ({ candidate, auth, members, onClose }) => {
    const [selectedMember, setSelectedMember] = useState(null);
    const [formData, setFormData] = useState({
        member_id: candidate?.member_id || '',
        description: candidate?.description || '',
    });
    const [imageFile, setImageFile] = useState(null);
    const [crop, setCrop] = useState(undefined);
    const [imageSrc, setImageSrc] = useState(null);
    const imgRef = useRef(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (candidate && candidate.member) {
            const member = members.find(m => m.id === candidate.member_id);
            if (member) setSelectedMember(member);
        }
    }, [candidate, members]);

    const handleMemberChange = (option) => {
        const member = members.find(m => m.id === option.value);
        setSelectedMember(member);
        setFormData(prev => ({ ...prev, member_id: option.value }));
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const onSelectFile = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const reader = new FileReader();
            reader.addEventListener('load', () => {
                setImageSrc(reader.result);
                const image = new Image();
                image.src = reader.result;
                image.onload = () => {
                    // Прямоугольная обрезка под портретную карточку (3:4)
                    const aspect = 3 / 4;
                    const defaultCrop = makeAspectCrop(
                        { width: 1200, height: 1200 / aspect },
                        { aspect },
                        image.width,
                        image.height
                    );
                    setCrop(centerCrop(defaultCrop, image.width, image.height));
                };
            });
            reader.readAsDataURL(e.target.files[0]);
            setImageFile(e.target.files[0]);
        }
    };

    const getCroppedFile = () => {
        if (!imgRef.current || !crop) return null;
        const canvas = document.createElement('canvas');
        const image = imgRef.current;
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        canvas.width = crop.width;
        canvas.height = crop.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(
            image,
            crop.x * scaleX,
            crop.y * scaleY,
            crop.width * scaleX,
            crop.height * scaleY,
            0,
            0,
            crop.width,
            crop.height
        );
        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                if (blob) {
                    const file = new File([blob], 'candidate.jpg', { type: 'image/jpeg' });
                    resolve(file);
                } else resolve(null);
            }, 'image/jpeg', 1.0);
        });
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const fd = new FormData();
            fd.append('member_id', formData.member_id);
            if (formData.description) fd.append('description', formData.description);
            if (imageFile) {
                const cropped = await getCroppedFile();
                if (cropped) fd.append('photo', cropped);
            }
            const config = { auth, headers: { 'Content-Type': 'multipart/form-data' } };
            if (candidate) {
                await electionApi.updateCandidate(candidate.id, fd, config);
            } else {
                await electionApi.createCandidate(fd, config);
            }
            onClose(true);
        } catch (err) {
            alert(err.response?.data?.detail || 'Ошибка сохранения');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <h3 className="text-xl font-bold mb-4">
                        {candidate ? 'Редактировать кандидата' : 'Новый кандидат'}
                    </h3>
                    <form onSubmit={handleSubmit}>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-gray-700 mb-1">Выберите гостя</label>
                                <Select
                                    options={members.map(m => ({
                                        value: m.id,
                                        label: `${m.last_name} ${m.first_name} ${m.patronymic || ''}`
                                    }))}
                                    value={selectedMember ? {
                                        value: selectedMember.id,
                                        label: `${selectedMember.last_name} ${selectedMember.first_name} ${selectedMember.patronymic || ''}`
                                    } : null}
                                    onChange={handleMemberChange}
                                    placeholder="Начните вводить имя..."
                                    isSearchable
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                />
                            </div>
                            <div>
                                <label className="block text-gray-700 mb-1">Описание</label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    rows="3"
                                    className="w-full p-2 border rounded"
                                />
                            </div>
                            <div>
                                <label className="block text-gray-700 mb-1">Фото (обязательно)</label>
                                <input type="file" accept="image/*" onChange={onSelectFile} className="w-full" />
                                {imageSrc && (
                                    <div className="mt-2">
                                        <ReactCrop crop={crop} onChange={(c) => setCrop(c)} aspect={3 / 4}>
                                            <img src={imageSrc} alt="Crop preview" ref={imgRef}
                                                 className="max-h-64 mx-auto"/>
                                        </ReactCrop>
                                    </div>
                                )}
                                {candidate?.photo_data && !imageSrc && (
                                    <div className="mt-2">
                                        <img src={candidate.photo_data} alt="current"
                                             className="h-20 object-cover rounded"/>
                                        <p className="text-xs text-gray-500">Текущее фото (замените новым, чтобы
                                            обновить)</p>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 mt-6">
                            <button type="button" onClick={() => onClose(false)} className="px-4 py-2 bg-gray-200 rounded">Отмена</button>
                            <button type="submit" disabled={loading} className="px-4 py-2 bg-gold-accent text-white rounded hover:bg-yellow-600 disabled:opacity-50">
                                {loading ? 'Сохранение...' : (candidate ? 'Сохранить' : 'Создать')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CandidateFormModal;